import React, { useState, useMemo } from 'react';
import {
    Plus, Search, SlidersHorizontal, ChevronDown, ChevronRight,
    Clock, Flag, MessageSquare, Paperclip, CheckCircle2,
    Circle, ArrowUpDown, MoreHorizontal, User as UserIcon,
    Calendar, Tag, Filter
} from 'lucide-react';
import { Issue, User, Project } from '../../types';

interface BordioTaskListProps {
    issues: Issue[];
    users: User[];
    projects: Project[];
    onIssueClick: (issue: Issue) => void;
    onCreateIssue: () => void;
}

type SortField = 'summary' | 'priority' | 'status' | 'dueDate' | 'updatedAt';
type SortDirection = 'asc' | 'desc';
type GroupBy = 'none' | 'status' | 'priority' | 'project' | 'assignee';

export const BordioTaskList: React.FC<BordioTaskListProps> = ({
    issues,
    users,
    projects,
    onIssueClick,
    onCreateIssue,
}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [sortField, setSortField] = useState<SortField>('updatedAt');
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
    const [groupBy, setGroupBy] = useState<GroupBy>('status');
    const [filterStatus, setFilterStatus] = useState<string | null>(null);
    const [filterPriority, setFilterPriority] = useState<string | null>(null);
    const [showFilters, setShowFilters] = useState(false);
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(['all']));

    const getUserById = (userId: string) => users.find(u => u.id === userId);
    const getProjectById = (projectId: string) => projects.find(p => p.id === projectId);

    const filteredAndSortedIssues = useMemo(() => {
        let result = [...issues];

        // Filter
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            result = result.filter(i =>
                i.summary.toLowerCase().includes(q) ||
                i.key.toLowerCase().includes(q) ||
                i.description?.toLowerCase().includes(q)
            );
        }
        if (filterStatus) result = result.filter(i => i.status === filterStatus);
        if (filterPriority) result = result.filter(i => i.priority === filterPriority);

        // Sort
        result.sort((a, b) => {
            let comparison = 0;
            switch (sortField) {
                case 'summary':
                    comparison = a.summary.localeCompare(b.summary);
                    break;
                case 'priority': {
                    const priorityOrder = { highest: 5, high: 4, medium: 3, low: 2, lowest: 1 };
                    comparison = (priorityOrder[b.priority] || 0) - (priorityOrder[a.priority] || 0);
                    break;
                }
                case 'status': {
                    const statusOrder = { backlog: 1, 'to-do': 2, 'in-progress': 3, 'in-review': 4, done: 5 };
                    comparison = (statusOrder[a.status] || 0) - (statusOrder[b.status] || 0);
                    break;
                }
                case 'dueDate':
                    comparison = (a.dueDate || '').localeCompare(b.dueDate || '');
                    break;
                case 'updatedAt':
                    comparison = new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
                    break;
            }
            return sortDirection === 'asc' ? comparison : -comparison;
        });

        return result;
    }, [issues, searchQuery, filterStatus, filterPriority, sortField, sortDirection]);

    // Group issues
    const groupedIssues = useMemo(() => {
        if (groupBy === 'none') {
            return [{ key: 'all', label: 'All Tasks', issues: filteredAndSortedIssues }];
        }

        const groups = new Map<string, Issue[]>();

        filteredAndSortedIssues.forEach(issue => {
            let key: string;
            switch (groupBy) {
                case 'status':
                    key = issue.status;
                    break;
                case 'priority':
                    key = issue.priority;
                    break;
                case 'project':
                    key = issue.projectId;
                    break;
                case 'assignee':
                    key = issue.assignee || 'unassigned';
                    break;
                default:
                    key = 'all';
            }
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key)!.push(issue);
        });

        return Array.from(groups.entries()).map(([key, issues]) => {
            let label = key;
            switch (groupBy) {
                case 'status':
                    label = key.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase());
                    break;
                case 'priority':
                    label = key.charAt(0).toUpperCase() + key.slice(1);
                    break;
                case 'project': {
                    const project = getProjectById(key);
                    label = project?.name || key;
                    break;
                }
                case 'assignee': {
                    if (key === 'unassigned') {
                        label = 'Unassigned';
                    } else {
                        const user = getUserById(key);
                        label = user?.displayName || key;
                    }
                    break;
                }
            }
            return { key, label, issues };
        });
    }, [filteredAndSortedIssues, groupBy]);

    const toggleGroup = (key: string) => {
        setExpandedGroups(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const toggleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'done':
                return <CheckCircle2 className="w-4 h-4 text-green-500" />;
            case 'in-progress':
                return <div className="w-4 h-4 rounded-full border-2 border-orange-400 border-t-transparent animate-spin" style={{ animationDuration: '3s' }} />;
            case 'in-review':
                return <div className="w-4 h-4 rounded-full border-2 border-purple-400" />;
            case 'to-do':
                return <Circle className="w-4 h-4 text-blue-400" />;
            default:
                return <Circle className="w-4 h-4 text-surface-300" />;
        }
    };

    const getPriorityIcon = (priority: string) => {
        const colors: Record<string, string> = {
            highest: 'text-red-600',
            high: 'text-orange-500',
            medium: 'text-yellow-500',
            low: 'text-blue-400',
            lowest: 'text-surface-400',
        };
        return <Flag className={`w-3.5 h-3.5 ${colors[priority] || 'text-surface-400'}`} />;
    };

    const getGroupDotColor = (key: string) => {
        if (groupBy === 'status') {
            switch (key) {
                case 'done': return 'bg-green-400';
                case 'in-progress': return 'bg-orange-400';
                case 'in-review': return 'bg-purple-400';
                case 'to-do': return 'bg-blue-400';
                default: return 'bg-surface-400';
            }
        }
        if (groupBy === 'priority') {
            switch (key) {
                case 'highest': return 'bg-red-500';
                case 'high': return 'bg-orange-500';
                case 'medium': return 'bg-yellow-500';
                case 'low': return 'bg-blue-400';
                default: return 'bg-surface-400';
            }
        }
        return 'bg-brand-400';
    };

    return (
        <div className="flex-1 flex flex-col overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-surface-100 bg-white">
                <div className="flex items-center gap-3">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400" />
                        <input
                            type="text"
                            placeholder="Search tasks..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="input-field pl-9 py-1.5 w-64 text-sm"
                        />
                    </div>

                    <button
                        onClick={() => setShowFilters(!showFilters)}
                        className={`btn-ghost text-xs gap-1.5 ${showFilters ? 'bg-brand-50 text-brand-600' : ''}`}
                    >
                        <SlidersHorizontal className="w-3.5 h-3.5" />
                        Filters
                    </button>

                    <div className="flex items-center gap-1 bg-surface-100 rounded-lg p-0.5">
                        {(['none', 'status', 'priority', 'project'] as GroupBy[]).map(g => (
                            <button
                                key={g}
                                onClick={() => {
                                    setGroupBy(g);
                                    setExpandedGroups(new Set(['all']));
                                }}
                                className={`px-2.5 py-1 text-2xs font-medium rounded-md transition-all ${groupBy === g ? 'bg-white text-surface-900 shadow-sm' : 'text-surface-500 hover:text-surface-700'
                                    }`}
                            >
                                {g === 'none' ? 'No Group' : g.charAt(0).toUpperCase() + g.slice(1)}
                            </button>
                        ))}
                    </div>
                </div>

                <button onClick={onCreateIssue} className="btn-primary py-1.5 text-sm">
                    <Plus className="w-4 h-4" />
                    Add Task
                </button>
            </div>

            {/* Filters */}
            {showFilters && (
                <div className="flex items-center gap-3 px-6 py-3 bg-surface-50 border-b border-surface-100 animate-slide-up">
                    <select
                        value={filterStatus || ''}
                        onChange={(e) => setFilterStatus(e.target.value || null)}
                        className="input-field py-1.5 w-40 text-sm"
                    >
                        <option value="">All Statuses</option>
                        <option value="backlog">Backlog</option>
                        <option value="to-do">To Do</option>
                        <option value="in-progress">In Progress</option>
                        <option value="in-review">In Review</option>
                        <option value="done">Done</option>
                    </select>
                    <select
                        value={filterPriority || ''}
                        onChange={(e) => setFilterPriority(e.target.value || null)}
                        className="input-field py-1.5 w-40 text-sm"
                    >
                        <option value="">All Priorities</option>
                        <option value="highest">Highest</option>
                        <option value="high">High</option>
                        <option value="medium">Medium</option>
                        <option value="low">Low</option>
                        <option value="lowest">Lowest</option>
                    </select>
                    {(filterStatus || filterPriority) && (
                        <button
                            onClick={() => { setFilterStatus(null); setFilterPriority(null); }}
                            className="btn-ghost text-xs text-red-600 hover:bg-red-50"
                        >
                            Clear
                        </button>
                    )}
                </div>
            )}

            {/* Table Header */}
            <div className="flex items-center px-6 py-2 bg-surface-50 border-b border-surface-100 text-xs font-semibold text-surface-500 uppercase tracking-wider">
                <div className="w-8" />
                <button onClick={() => toggleSort('summary')} className="flex-1 flex items-center gap-1 hover:text-surface-700 transition-colors text-left">
                    Task {sortField === 'summary' && <ArrowUpDown className="w-3 h-3" />}
                </button>
                <button onClick={() => toggleSort('status')} className="w-28 flex items-center gap-1 hover:text-surface-700 transition-colors">
                    Status {sortField === 'status' && <ArrowUpDown className="w-3 h-3" />}
                </button>
                <button onClick={() => toggleSort('priority')} className="w-24 flex items-center gap-1 hover:text-surface-700 transition-colors">
                    Priority {sortField === 'priority' && <ArrowUpDown className="w-3 h-3" />}
                </button>
                <div className="w-32">Assignee</div>
                <button onClick={() => toggleSort('dueDate')} className="w-28 flex items-center gap-1 hover:text-surface-700 transition-colors">
                    Due Date {sortField === 'dueDate' && <ArrowUpDown className="w-3 h-3" />}
                </button>
                <div className="w-24">Project</div>
                <div className="w-8" />
            </div>

            {/* Task List */}
            <div className="flex-1 overflow-y-auto">
                {groupedIssues.map(group => (
                    <div key={group.key}>
                        {groupBy !== 'none' && (
                            <button
                                onClick={() => toggleGroup(group.key)}
                                className="flex items-center gap-2 w-full px-6 py-2.5 bg-surface-50/50 border-b border-surface-100 hover:bg-surface-100/50 transition-colors"
                            >
                                {expandedGroups.has(group.key) || expandedGroups.has('all') ? (
                                    <ChevronDown className="w-3.5 h-3.5 text-surface-400" />
                                ) : (
                                    <ChevronRight className="w-3.5 h-3.5 text-surface-400" />
                                )}
                                <div className={`w-2 h-2 rounded-full ${getGroupDotColor(group.key)}`} />
                                <span className="text-sm font-semibold text-surface-700">{group.label}</span>
                                <span className="text-xs text-surface-400 bg-surface-100 px-1.5 py-0.5 rounded-full">
                                    {group.issues.length}
                                </span>
                            </button>
                        )}

                        {(expandedGroups.has(group.key) || expandedGroups.has('all') || groupBy === 'none') && (
                            <div>
                                {group.issues.map(issue => {
                                    const assignee = issue.assignee ? getUserById(issue.assignee) : null;
                                    const project = getProjectById(issue.projectId);
                                    const isOverdue = issue.dueDate && new Date(issue.dueDate) < new Date() && issue.status !== 'done';

                                    return (
                                        <button
                                            key={issue.id}
                                            onClick={() => onIssueClick(issue)}
                                            className="flex items-center w-full px-6 py-3 border-b border-surface-50 hover:bg-surface-50 transition-colors text-left group"
                                        >
                                            <div className="w-8 flex-shrink-0">
                                                {getStatusIcon(issue.status)}
                                            </div>
                                            <div className="flex-1 min-w-0 pr-4">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-2xs text-surface-400 font-medium">{issue.key}</span>
                                                    <p className="text-sm font-medium text-surface-800 truncate">{issue.summary}</p>
                                                </div>
                                                {issue.labels && issue.labels.length > 0 && (
                                                    <div className="flex items-center gap-1 mt-0.5">
                                                        {issue.labels.slice(0, 2).map((label, i) => (
                                                            <span key={i} className="text-2xs bg-surface-100 text-surface-500 px-1.5 py-0.5 rounded">
                                                                {label}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="w-28 flex-shrink-0">
                                                <span className={`badge text-2xs ${issue.status === 'done' ? 'badge-green' :
                                                        issue.status === 'in-progress' ? 'badge-orange' :
                                                            issue.status === 'in-review' ? 'badge-purple' :
                                                                issue.status === 'to-do' ? 'badge-blue' :
                                                                    'badge-gray'
                                                    }`}>
                                                    {issue.status.replace('-', ' ')}
                                                </span>
                                            </div>
                                            <div className="w-24 flex-shrink-0">
                                                {getPriorityIcon(issue.priority)}
                                            </div>
                                            <div className="w-32 flex-shrink-0">
                                                {assignee ? (
                                                    <div className="flex items-center gap-2">
                                                        {assignee.avatarUrl ? (
                                                            <img src={assignee.avatarUrl} alt="" className="w-5 h-5 rounded-full" />
                                                        ) : (
                                                            <div className="avatar avatar-sm">{assignee.displayName.charAt(0)}</div>
                                                        )}
                                                        <span className="text-xs text-surface-600 truncate">{assignee.displayName.split(' ')[0]}</span>
                                                    </div>
                                                ) : (
                                                    <span className="text-xs text-surface-400">—</span>
                                                )}
                                            </div>
                                            <div className="w-28 flex-shrink-0">
                                                {issue.dueDate ? (
                                                    <span className={`text-xs flex items-center gap-1 ${isOverdue ? 'text-red-600 font-medium' : 'text-surface-500'}`}>
                                                        <Calendar className="w-3 h-3" />
                                                        {new Date(issue.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                                    </span>
                                                ) : (
                                                    <span className="text-xs text-surface-400">—</span>
                                                )}
                                            </div>
                                            <div className="w-24 flex-shrink-0">
                                                <span className="text-xs text-surface-500 truncate">{project?.key || ''}</span>
                                            </div>
                                            <div className="w-8 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <MoreHorizontal className="w-4 h-4 text-surface-400" />
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                ))}

                {filteredAndSortedIssues.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-16">
                        <div className="w-16 h-16 bg-surface-100 rounded-2xl flex items-center justify-center mb-4">
                            <CheckCircle2 className="w-8 h-8 text-surface-300" />
                        </div>
                        <h3 className="text-base font-semibold text-surface-700 mb-1">No tasks found</h3>
                        <p className="text-sm text-surface-500 mb-4">
                            {searchQuery ? 'Try adjusting your search or filters' : 'Create your first task to get started'}
                        </p>
                        <button onClick={onCreateIssue} className="btn-primary text-sm">
                            <Plus className="w-4 h-4" />
                            Create Task
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};
